import {
  isDrivingLicenseDocType,
  isIdentityCardDocType,
  isPassportDocType,
  type DocumentChoice,
  type DocumentGenericCode,
  type DocumentGenericType,
  type MrzSide,
} from '@chekin/core';
import {DOCUMENT_CATALOG, DOCUMENT_TYPE_CATALOG} from './catalog';
import {DOCUMENT_LAYOUTS} from './layouts';
import type {
  DocumentCatalogEntry,
  DocumentSide,
  DocumentVisual,
  DocumentVisualKind,
  MrzRect,
} from './types';

const GENERIC_ENTRIES: Record<DocumentVisualKind, DocumentCatalogEntry> = {
  id: {
    side: 'back',
    image: 'generic-id-back',
    other: 'generic-id-front',
    mrz: [4, 62, 92, 28],
  },
  passport: {
    side: 'data',
    image: 'generic-passport',
    mrz: [3, 76, 94, 17],
  },
};

const MRZ_SIDE_TO_DOCUMENT_SIDE: Record<MrzSide, DocumentSide> = {
  front: 'front',
  back: 'back',
  data_page: 'data',
};

type ResolveDocumentVisualArgs = {
  countryCode?: string | null;
  documentChoice?: Pick<
    DocumentChoice,
    'value' | 'without_mrz' | 'generic_type' | 'mrz_side'
  > | null;
};

type ResolvedKind = DocumentVisualKind | 'none';

const GENERIC_TYPES: Record<
  DocumentGenericType | DocumentGenericCode,
  DocumentGenericType
> = {
  passport: 'passport',
  identity_card: 'identity_card',
  residence_permit: 'residence_permit',
  driving_license: 'driving_license',
  other: 'other',
  P: 'passport',
  I: 'identity_card',
  DL: 'driving_license',
  PI: 'other',
  SD: 'other',
  V: 'other',
};

function normalizeGenericType(
  genericType?: DocumentGenericType | DocumentGenericCode | null,
): DocumentGenericType | null {
  return (genericType && GENERIC_TYPES[genericType]) ?? null;
}

function resolveKind(
  value: string,
  genericType: DocumentGenericType | null,
): ResolvedKind {
  if (isPassportDocType(value)) return 'passport';
  if (isDrivingLicenseDocType(value)) return 'none';
  if (genericType === 'passport') return 'passport';
  if (genericType === 'driving_license') return 'none';
  return 'id';
}

function hasCountryVisual(
  value: string,
  genericType: DocumentGenericType | null,
  kind: DocumentVisualKind,
) {
  if (kind === 'passport') return true;
  return isIdentityCardDocType(value) || genericType === 'identity_card';
}

function getLayoutMrz(image: string): MrzRect | undefined {
  const zone = DOCUMENT_LAYOUTS[image]?.find(([type]) => type === 'mrz');
  return zone && [zone[1], zone[2], zone[3], zone[4]];
}

function applySide<Entry extends DocumentCatalogEntry>(
  entry: Entry,
  mrzSide?: MrzSide,
): Entry | null {
  if (!mrzSide) return entry;
  const side = MRZ_SIDE_TO_DOCUMENT_SIDE[mrzSide];
  if (side === entry.side || entry.side === 'lines') return entry;
  const otherMrz = entry.other && getLayoutMrz(entry.other);
  if (!entry.other || !otherMrz) return null;
  return {
    ...entry,
    side,
    image: entry.other,
    other: entry.image,
    mrz: otherMrz,
  };
}

function resolveDocumentVisual({
  countryCode,
  documentChoice,
}: ResolveDocumentVisualArgs): DocumentVisual | null {
  if (!documentChoice) return null;

  const documentTypeEntry = DOCUMENT_TYPE_CATALOG[documentChoice.value];
  if (documentTypeEntry) return applySide(documentTypeEntry, documentChoice.mrz_side);
  if (documentChoice.without_mrz) return null;

  const genericType = normalizeGenericType(documentChoice.generic_type);
  const kind = resolveKind(documentChoice.value, genericType);
  if (kind === 'none') return null;

  const country = countryCode ? DOCUMENT_CATALOG[countryCode.toUpperCase()] : undefined;
  const countryEntry = hasCountryVisual(documentChoice.value, genericType, kind)
    ? country?.[kind]
    : undefined;
  const entry = applySide(countryEntry ?? GENERIC_ENTRIES[kind], documentChoice.mrz_side);

  return entry && {kind, ...entry};
}

const NO_MRZ: MrzRect = [0, 0, 0, 0];

function getSideVisual(
  visual: DocumentVisual | null,
  side: 'front' | 'back',
): DocumentVisual | null {
  if (!visual) return null;
  if (visual.kind === 'passport' || !visual.other) return visual;
  if (visual.side === side || visual.side === 'lines' || visual.side === 'data') {
    return visual;
  }
  return {
    ...visual,
    side,
    image: visual.other,
    other: visual.image,
    mrz: getLayoutMrz(visual.other) ?? NO_MRZ,
  };
}

function hasDocumentTypeVisual(value?: string | null) {
  return Boolean(value && DOCUMENT_TYPE_CATALOG[value]);
}

export {getSideVisual, hasDocumentTypeVisual, resolveDocumentVisual};
